import { useCallback, useEffect, useState } from 'react';
import { api, errorText, formatDateTime, formatQty } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, CheckField, Empty, Loading, Notice, TextField } from '../components/ui';

interface Meldung {
  id: string;
  article_id: string;
  sku: string;
  article_name: string;
  unit: string;
  owner_id: string;
  owner_name: string;
  status: 'offen' | 'quittiert' | 'behoben';
  erkannt_am: string;
  bestand: number;
  min_stock: number;
  fehlmenge: number;
  verbrauch_je_tag: number;
  reichweite_tage: number | null;
  quittiert_am: string | null;
  quittiert_von_name: string | null;
  notiz: string | null;
  behoben_am: string | null;
}

interface Einstellungen {
  aktiv: boolean;
  empfaenger: string | null;
  auch_auftraggeber: boolean;
  zuletzt_gesendet: string | null;
}

/** Wie dringend ist das? Die Reichweite entscheidet, nicht die Fehlmenge. */
function dringlichkeit(m: Meldung): { kind: 'bad' | 'warn' | 'info'; text: string } {
  if (m.reichweite_tage === null) return { kind: 'info', text: 'kein Verbrauch' };
  if (m.reichweite_tage <= 3) return { kind: 'bad', text: `${m.reichweite_tage} Tage` };
  if (m.reichweite_tage <= 14) return { kind: 'warn', text: `${m.reichweite_tage} Tage` };
  return { kind: 'info', text: `${m.reichweite_tage} Tage` };
}

export function AlertsPage() {
  const { can } = useAuth();
  const darfPflegen = can('admin', 'kaufmann', 'disponent');

  const [meldungen, setMeldungen] = useState<Meldung[] | null>(null);
  const [einstellungen, setEinstellungen] = useState<Einstellungen | null>(null);
  const [mitBehobenen, setMitBehobenen] = useState(false);
  const [empfaenger, setEmpfaenger] = useState('');
  const [notiz, setNotiz] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const daten = await api<{ meldungen: Meldung[]; einstellungen: Einstellungen }>(
        `/api/articles/meldungen?mitBehobenen=${mitBehobenen}`,
      );
      setMeldungen(daten.meldungen);
      setEinstellungen(daten.einstellungen);
      setEmpfaenger(daten.einstellungen.empfaenger ?? '');
    } catch (err) {
      setError(errorText(err));
    }
  }, [mitBehobenen]);

  useEffect(() => {
    void load();
  }, [load]);

  async function jetztPruefen() {
    setBusy(true);
    setError('');
    try {
      const ergebnis = await api<{ neu: Meldung[]; behoben: number; offen: number }>(
        '/api/articles/meldungen/pruefen',
        { method: 'POST' },
      );
      setMessage(
        `${ergebnis.neu.length} neue Meldung(en), ${ergebnis.behoben} erledigt, ` +
          `${ergebnis.offen} offen.`,
      );
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function quittieren(id: string) {
    setBusy(true);
    setError('');
    try {
      await api(`/api/articles/meldungen/${id}/quittieren`, {
        method: 'POST',
        body: { notiz: notiz[id]?.trim() || null },
      });
      setNotiz((alt) => ({ ...alt, [id]: '' }));
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function einstellungenSpeichern(aktiv: boolean, auchAuftraggeber: boolean) {
    setBusy(true);
    setError('');
    try {
      const neu = await api<Einstellungen>('/api/articles/meldungen/einstellungen', {
        method: 'PUT',
        body: { aktiv, empfaenger: empfaenger.trim() || null, auchAuftraggeber },
      });
      setEinstellungen(neu);
      setMessage('Einstellungen gespeichert.');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (!meldungen || !einstellungen) {
    return error ? <Notice kind="error">{error}</Notice> : <Loading />;
  }

  const offene = meldungen.filter((m) => !m.behoben_am);
  const dringend = offene.filter((m) => m.reichweite_tage !== null && m.reichweite_tage <= 3);

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      {dringend.length > 0 && (
        <Notice kind="error">
          <strong>
            {dringend.length} Artikel {dringend.length === 1 ? 'ist' : 'sind'} in drei Tagen
            aufgebraucht.
          </strong>{' '}
          Gerechnet aus dem Abgang der letzten Wochen, nicht aus dem Mindestbestand.
        </Notice>
      )}

      <Card title="Meldungen zum Mindestbestand" padded={false}>
        <div className="body">
          <div className="small muted" style={{ marginBottom: 12 }}>
            Sortiert nach <strong>Reichweite</strong> — wie lange der Bestand beim Verbrauch der
            letzten Wochen noch hält. Das ist die ehrlichere Reihenfolge als die Fehlmenge: Ein
            Artikel knapp unter der Grenze, den niemand abruft, hat Zeit; einer weit darüber, der
            täglich rausgeht, nicht.
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {darfPflegen && (
              <button onClick={jetztPruefen} disabled={busy}>
                Jetzt prüfen
              </button>
            )}
            <CheckField
              label="Erledigte mit anzeigen"
              checked={mitBehobenen}
              onChange={setMitBehobenen}
            />
          </div>
        </div>

        {meldungen.length === 0 ? (
          <Empty>Kein Artikel unterschreitet seinen Mindestbestand.</Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Eigentümer</th>
                  <th className="num">Bestand</th>
                  <th className="num">Mindest</th>
                  <th className="num">Verbrauch/Tag</th>
                  <th>Reicht noch</th>
                  <th>Stand</th>
                  {darfPflegen && <th>Erledigen</th>}
                </tr>
              </thead>
              <tbody>
                {meldungen.map((m) => {
                  const d = dringlichkeit(m);
                  return (
                    <tr key={m.id} style={m.behoben_am ? { opacity: 0.55 } : undefined}>
                      <td>
                        <span className="mono">{m.sku}</span> {m.article_name}
                        <div className="small muted">seit {formatDateTime(m.erkannt_am)}</div>
                      </td>
                      <td className="small">{m.owner_name}</td>
                      <td className="num" style={{ color: 'var(--bad)' }}>
                        {formatQty(m.bestand, m.unit)}
                      </td>
                      <td className="num muted">{formatQty(m.min_stock)}</td>
                      <td className="num muted">
                        {m.verbrauch_je_tag > 0 ? formatQty(m.verbrauch_je_tag) : '—'}
                      </td>
                      <td>
                        <Badge kind={d.kind}>{d.text}</Badge>
                      </td>
                      <td className="small">
                        {m.behoben_am ? (
                          <Badge kind="ok">wieder gedeckt</Badge>
                        ) : m.status === 'quittiert' ? (
                          <>
                            <Badge kind="info">bekannt</Badge>
                            <div className="muted">
                              {m.quittiert_von_name}
                              {m.notiz && ` · ${m.notiz}`}
                            </div>
                          </>
                        ) : (
                          <Badge kind="warn">offen</Badge>
                        )}
                      </td>
                      {darfPflegen && (
                        <td>
                          {!m.behoben_am && m.status !== 'quittiert' && (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <input
                                value={notiz[m.id] ?? ''}
                                onChange={(e) =>
                                  setNotiz((alt) => ({ ...alt, [m.id]: e.target.value }))
                                }
                                placeholder="bestellt am …"
                                style={{ width: 130 }}
                              />
                              <button
                                className="small"
                                onClick={() => quittieren(m.id)}
                                disabled={busy}
                              >
                                Quittieren
                              </button>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {can('admin', 'kaufmann') && (
        <Card title="Benachrichtigung">
          <div className="small muted" style={{ marginBottom: 14 }}>
            Einmal täglich um 05:45 Uhr, und <strong>nur wenn etwas Neues dazugekommen ist</strong>.
            Eine Nachricht, die jeden Morgen dieselbe Liste bringt, wird nach einer Woche ungelesen
            weggeklickt — und dann fällt auch die eine wichtige Zeile nicht mehr auf.
          </div>

          <div className="form-grid">
            <TextField
              label="Empfänger"
              value={empfaenger}
              onChange={setEmpfaenger}
              placeholder="einkauf@firma.de, lager@firma.de"
              hint="mehrere durch Komma getrennt"
            />
          </div>

          <CheckField
            label="Benachrichtigung eingeschaltet"
            checked={einstellungen.aktiv}
            onChange={(v) => void einstellungenSpeichern(v, einstellungen.auch_auftraggeber)}
          />
          <CheckField
            label="Auftraggeber über ihre eigene Ware unterrichten"
            checked={einstellungen.auch_auftraggeber}
            onChange={(v) => void einstellungenSpeichern(einstellungen.aktiv, v)}
          />
          <div className="small muted" style={{ marginBottom: 12 }}>
            Jeder Auftraggeber bekommt dabei ausschliesslich seine eigenen Artikel — an die
            Adresse, die am Partner hinterlegt ist.
          </div>

          <button className="primary" onClick={() => void einstellungenSpeichern(einstellungen.aktiv, einstellungen.auch_auftraggeber)} disabled={busy}>
            Empfänger speichern
          </button>

          <div className="small muted" style={{ marginTop: 12 }}>
            {einstellungen.zuletzt_gesendet
              ? `Zuletzt versendet: ${formatDateTime(einstellungen.zuletzt_gesendet)}`
              : 'Es wurde noch nichts versendet.'}
            {' · '}Der Versand läuft über die Einstellungen unter <em>Belegversand</em>; solange
            dort der Testmodus an ist, wird nur protokolliert.
          </div>
        </Card>
      )}
    </>
  );
}
